import { FC, RefObject, useEffect, useState } from 'react'

import styles from './LabRevealOverlay.module.sass'

interface LabRevealOverlayProps {
  /** The image being replaced — wiped away top to bottom */
  previousImageUrl: string
  /** The preview frame (the box the photo is contain-fitted into) */
  frameRef: RefObject<HTMLElement>
  /** Natural aspect ratio (width / height) of the photo, to reproduce the contain-fit rectangle */
  aspectRatio?: number
  onDone: () => void
}

interface Rect { left: number, top: number, width: number, height: number }

/** The letterboxed rectangle an object-fit: contain image paints into, inside a frame of the given size. */
const getContainRect = (frame: HTMLElement, aspectRatio?: number): Rect | null => {
  const { clientWidth, clientHeight } = frame
  if (!clientWidth || !clientHeight) return null
  if (!aspectRatio) return { left: 0, top: 0, width: clientWidth, height: clientHeight }
  const width = Math.min(clientWidth, clientHeight * aspectRatio)
  const height = width / aspectRatio
  return { left: (clientWidth - width) / 2, top: (clientHeight - height) / 2, width, height }
}

/**
 * Plays the Playground-style reveal over a freshly shown Lab result: the previous image sits on top of
 * the new one and is clipped away from the top while a glowing line follows the edge. Sized to the
 * photo's painted rectangle so the wipe never spills into the letterbox — it works over the plain
 * image and over the before/after slider alike.
 */
export const LabRevealOverlay: FC<LabRevealOverlayProps> = ({ previousImageUrl, frameRef, aspectRatio, onDone }) => {
  const [rect, setRect] = useState<Rect | null>(null)

  useEffect(() => {
    const measure = () => {
      const frame = frameRef.current
      if (frame) setRect(getContainRect(frame, aspectRatio))
    }
    measure()
    window.addEventListener('resize', measure)
    // Safety net in case `animationend` never fires (tab hidden, element unmounted mid-way)
    const timeout = window.setTimeout(onDone, 2000)
    return () => {
      window.removeEventListener('resize', measure)
      window.clearTimeout(timeout)
    }
  }, [aspectRatio, frameRef, onDone])

  if (!rect) return null

  return (
    <div
      style={{ position: 'absolute', left: rect.left, top: rect.top, width: rect.width, height: rect.height, overflow: 'hidden', pointerEvents: 'none', zIndex: 2 }}
      aria-hidden="true"
    >

      <img src={previousImageUrl} alt="" className={styles.previous} onAnimationEnd={onDone} />

      <div className={styles.line} />

    </div>
  )
}
